(function (root) {
  'use strict';
  const PREFIX = '_mrv_bf_';
  const PAUSED = '_mrv_bf_paused';
  const prop = (item, name) => item.properties?.[PREFIX + name];
  const quantity = items => items.reduce((n, item) => n + Number(item.quantity), 0);
  const stable = value => JSON.stringify(Object.fromEntries(Object.entries(value || {}).sort(([a], [b]) => a.localeCompare(b))));
  const identity = item => [String(item.variant_id || item.id), stable(item.properties), String(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || '')].join('|');
  const variantRule = (rule, variant) => rule?.variants ? rule.variants[String(variant)] : rule;
  const itemRule = (item, rules) => variantRule(rules[prop(item, 'offer')], item.variant_id || item.id);
  const active = (rule, now = Date.now()) => rule && rule.active && (!rule.starts || now >= Date.parse(rule.starts)) && (!rule.ends || now < Date.parse(rule.ends));
  const userProperties = item => Object.fromEntries(Object.entries(item.properties || {}).filter(([key]) => !key.startsWith(PREFIX)));
  function displayItems(cart, item) {
    const group = prop(item, 'group');
    if (!group) return cart.items.filter(i => identity(i) === identity(item));
    const planId = i => String(i.selling_plan_allocation?.selling_plan?.id || i.selling_plan || '');
    return cart.items.filter(i => prop(i, 'group') === group && String(i.variant_id || i.id) === String(item.variant_id || item.id) && planId(i) === planId(item));
  }
  function nativeFreeUnits(item, rule) {
    if (!rule || !Number(item.original_price)) return 0;
    const amount = (item.line_level_discount_allocations || []).filter(a => a.discount_application?.title === rule.title).reduce((sum, a) => sum + Number(a.amount), 0);
    return Math.min(item.quantity, Math.floor(amount / item.original_price));
  }
  function legacyOffer(item, rules) {
    if (prop(item, 'source') || prop(item, 'role') || item.selling_plan_allocation || item.selling_plan) return null;
    for (const [offer, parent] of Object.entries(rules)) {
      const rule = variantRule(parent, item.variant_id || item.id);
      if (active(rule) && rule.products.includes(String(item.product_id))) return { offer, rule };
    }
    return null;
  }
  function taggedProperties(item, offer, rule, role, source) {
    const properties = userProperties(item);
    const group = encodeURIComponent([rule.id, item.variant_id || item.id, stable(properties), ...(source === 'cart-upsell' ? [source] : [])].join(':'));
    return { ...properties, [PREFIX + 'source']: source, [PREFIX + 'offer']: offer, [PREFIX + 'role']: role, [PREFIX + 'group']: group };
  }
  function paused(cart) {
    try { return JSON.parse(cart.attributes?.[PAUSED] || '{}'); } catch { return {}; }
  }
  function verified(item, rule) {
    return Number(item.final_line_price) === 0 && Number(item.original_line_price) > 0 &&
      (item.line_level_discount_allocations || []).some(a =>
        a.discount_application?.title === rule.title && Number(a.amount) > 0);
  }
  function groups(cart, rules) {
    const result = new Map();
    for (const item of cart.items) {
      const group = prop(item, 'group');
      if (!group || !['product-page', 'legacy-cart', 'cart-upsell'].includes(prop(item, 'source'))) continue;
      const rule = itemRule(item, rules);
      if (!rule || !rule.products.includes(String(item.product_id))) continue;
      if (!result.has(group)) result.set(group, { id: group, rule, paid: [], gifts: [], variant: item.variant_id });
      const g = result.get(group);
      if (g.variant !== item.variant_id || g.rule !== rule) continue;
      if (prop(item, 'role') === 'paid') g.paid.push(item);
      if (prop(item, 'role') === 'gift') g.gifts.push(item);
    }
    return [...result.values()];
  }
  function plan(cart, rules, now = Date.now()) {
    const declined = paused(cart);
    const budget = new Map();
    const all = groups(cart, rules).sort((a, b) => quantity(b.gifts) - quantity(a.gifts));
    const result = [];
    for (const group of all) {
      const rule = group.rule;
      let available = Infinity;
      if (rule.limit !== null) {
        if (!budget.has(rule.id)) {
          const unmanaged = cart.items.filter(i => rule.products.includes(String(i.product_id)) && prop(i, 'role') !== 'paid' && prop(i, 'role') !== 'gift');
          budget.set(rule.id, Math.max(0, rule.limit - Math.floor(quantity(unmanaged) / (rule.buy + rule.free))));
        }
        available = budget.get(rule.id);
      }
      const current = quantity(group.gifts);
      const inline = current ? 0 : group.paid.reduce((n, i) => n + nativeFreeUnits(i, rule), 0);
      const paid = Math.max(0, quantity(group.paid) - inline);
      const cycles = active(rule, now) ? Math.min(Math.floor(paid / rule.buy), available) : 0;
      const capacity = Math.max(0, cycles * rule.free - inline);
      const target = declined[group.id] ? 0 : Math.min(current, capacity);
      if (available !== Infinity) budget.set(rule.id, available - cycles);
      result.push({ ...group, target, capacity, current, reason: declined[group.id] || '' });
    }
    return result;
  }
  function claimOffer(cart, item, rules) {
    if (item && prop(item, 'role') === 'bundle') item = displayItems(cart, item).find(i => prop(i, 'role') === 'paid') || item;
    if (!item || item.selling_plan_allocation || item.selling_plan || Number(item.original_price) <= 0) return null;
    const original = identity(item);
    let offer = legacyOffer(item, rules);
    if (!offer && ['cart-upsell', 'other'].includes(prop(item, 'source')) && !prop(item, 'role')) {
      offer = legacyOffer({ ...item, properties: userProperties(item) }, rules);
    }
    if (!offer && ['product-page', 'legacy-cart', 'cart-upsell'].includes(prop(item, 'source')) && prop(item, 'role') === 'paid') {
      offer = { offer: prop(item, 'offer'), rule: itemRule(item, rules) };
    }
    const rule = offer?.rule;
    if (!active(rule) || !rule.products.includes(String(item.product_id)) || rule.buy !== 2 || ![1, 2].includes(rule.free)) return null;
    const source = prop(item, 'source') === 'other' ? 'cart-upsell' : prop(item, 'source') || 'legacy-cart';
    const properties = taggedProperties(item, offer.offer, rule, 'paid', source);
    const group = properties[PREFIX + 'group'];
    const single = quantity(cart.items.filter(i => identity(i) === original)) === 1;
    const simulated = { ...cart, items: cart.items.map(i => identity(i) === original ? { ...i, quantity: i.quantity + (single ? 1 : 0), properties } : i) };
    const target = plan(simulated, rules).find(g => g.id === group);
    const more = target ? target.capacity - target.current : 0;
    if (more <= 0) return null;
    if (single && (quantity(target.paid) !== 2 || target.current || more !== rule.free)) return null;
    return { ...offer, item, properties, group, more, mode: single ? 'upgrade' : 'free', price: single ? Number(item.original_price) : 0, current: target.current, total: quantity(displayItems(cart, item)) + more + (single ? 1 : 0) };
  }
  function packOffer(cart, item, pack) {
    if (!pack || !item || String(item.product_id) !== String(pack.product) || item.selling_plan || item.selling_plan_allocation || prop(item, 'role') === 'gift') return null;
    const from = pack.variants[String(item.variant_id)];
    const to = pack.variants[String(pack.target)];
    if (!from || !to?.available || from.uses >= to.uses || from.price <= 0 || to.price <= from.price) return null;
    const count = quantity(cart.items.filter(i => identity(i) === identity(item)));
    if (!count || cart.items.some(i => identity(i) === identity(item) && Number(i.original_price) !== from.price)) return null;
    return { mode: 'pack', target: Number(pack.target), count, more: (to.uses - from.uses) * count, price: (to.price - from.price) * count, from, to };
  }
  function previewQuantity(cart, item, delta, rules, isClaim = false) {
    if (isClaim) return claimOffer(cart, item, rules)?.total || item.quantity;
    const siblings = displayItems(cart, item).filter(i => prop(i, 'role') !== 'gift');
    const native = siblings.reduce((n, i) => n + nativeFreeUnits(i, itemRule(item, rules)), 0);
    const paid = Math.max(0, quantity(siblings) - native + delta);
    const rule = itemRule(item, rules);
    const group = groups(cart, rules).find(g => g.id === prop(item, 'group'));
    const current = quantity(group?.gifts || []) + native;
    const capacity = active(rule) ? Math.min(Math.floor(paid / rule.buy), rule.limit ?? Infinity) * rule.free : 0;
    return paid + Math.min(current, capacity);
  }
  // A private line property records the first successful addition atomically.
  // Unlike cart attributes, it cannot survive an external /cart/clear request.
  // Missing markers on older carts deliberately do not imply a bundle-first cart.
  function glowCartScope(cart, config) {
    if (!config?.enabled || !config.originProperty || !config.originValue) return false;
    return Boolean(cart?.items?.some(item => {
      const properties = Array.isArray(item.properties) ? Object.fromEntries(item.properties) : item.properties;
      return Number(item.variant_id || item.id) === Number(config.variant) &&
        String(item.product_id) === String(config.product) && item.quantity > 0 &&
        !item.selling_plan && !item.selling_plan_allocation &&
        properties?.[config.originProperty] === config.originValue;
    }));
  }
  function glowOriginItems(cart, items, config) {
    if (!config?.enabled || !config.originProperty || !config.originValue) return items;
    const isKit = item => Number(item.variant_id || item.id) === Number(config.variant) &&
      Number(item.quantity) > 0 && !item.selling_plan && !item.selling_plan_allocation;
    const empty = !cart.items.some(item => item.quantity > 0);
    // A mixed multi-add has no reliable customer-facing first-product order.
    const startedHere = empty && items.length > 0 && items.every(isKit);
    const scoped = startedHere || glowCartScope(cart, config);
    return items.map(item => {
      if (Number(item.variant_id || item.id) !== Number(config.variant)) return item;
      const properties = { ...item.properties };
      delete properties[config.originProperty];
      if (scoped && isKit(item)) properties[config.originProperty] = config.originValue;
      return { ...item, properties };
    });
  }
  function glowUpsellHandles(cart, config) {
    if (!glowCartScope(cart, config)) return null;
    const present = new Set(cart.items.filter(item => item.quantity > 0).map(item => item.handle));
    return [ ...(glowSecondOffer(cart, config) ? ['__glow_second_kit__'] : []),
      ...(config.upsells || []).filter(handle => !present.has(handle)) ];
  }
  const codeKey = code => String(code || '').trim().toUpperCase();
  const lineProperties = item => Array.isArray(item.properties) ? Object.fromEntries(item.properties) : (item.properties || {});
  const glowKits = (cart, config) => (cart?.items || []).filter(item =>
    Number(item.variant_id || item.id) === Number(config?.variant) && String(item.product_id) === String(config?.product));
  const glowSecondItems = (cart, config) => glowKits(cart, config).filter(item =>
    Boolean(lineProperties(item)[config.secondKit?.property]));
  function cartCodes(cart) {
    if (!Array.isArray(cart.discount_codes)) throw new Error('Discount codes could not be checked. Please try again.');
    return [...new Map(cart.discount_codes.filter(d => d.code).map(d => [codeKey(d.code), d.code])).values()];
  }
  function glowCodeAmount(cart, config) {
    const code = codeKey(config.secondKit?.discountCode);
    return glowKits(cart, config).reduce((sum, item) => sum + (item.line_level_discount_allocations || [])
      .filter(a => codeKey(a.discount_application?.title) === code)
      .reduce((n, a) => n + Number(a.amount || 0), 0), 0);
  }
  function glowSecondOffer(cart, config) {
    const offer = config?.secondKit;
    if (!offer?.enabled || !offer.verified || !offer.discountCode || !glowCartScope(cart, config) ||
      cart.currency !== (offer.currency || 'ZAR') || glowSecondItems(cart, config).length) return null;
    const kits = glowKits(cart, config);
    if (quantity(kits) !== 1 || kits.some(i => i.selling_plan || i.selling_plan_allocation || Number(i.original_price) !== offer.basePrice) ||
      glowCodeAmount(cart, config) > 0 || offer.basePrice - offer.amount !== offer.price) return null;
    return offer;
  }
  function glowSecondVerified(cart, config) {
    const offer = config?.secondKit;
    if (!offer?.discountCode || !glowCartScope(cart, config) || cart.currency !== (offer.currency || 'ZAR')) return false;
    const kits = glowKits(cart, config), seconds = glowSecondItems(cart, config);
    const base = kits.filter(item => !seconds.includes(item));
    if (quantity(seconds) !== 1 || quantity(base) < 1 || kits.some(i => i.selling_plan || i.selling_plan_allocation || Number(i.original_price) !== offer.basePrice) ||
      seconds.some(i => codeKey(lineProperties(i)[offer.property]) !== codeKey(offer.discountCode) || Number(lineProperties(i)._mrv_glow_second_price) !== offer.price)) return false;
    if (!cart.discount_codes?.some(d => codeKey(d.code) === codeKey(offer.discountCode) && d.applicable === true) ||
      glowCodeAmount(cart, config) !== offer.amount) return false;
    const elsewhere = cart.items.filter(i => !kits.includes(i)).some(i => (i.line_level_discount_allocations || [])
      .some(a => codeKey(a.discount_application?.title) === codeKey(offer.discountCode) && Number(a.amount) > 0));
    return !elsewhere && kits.reduce((sum, i) => sum + Number(i.final_line_price), 0) <= quantity(kits) * offer.basePrice - offer.amount;
  }
  function preservedCodes(before, after, except = '', requireApplicable = true) {
    const actual = new Map((after.discount_codes || []).map(d => [codeKey(d.code), d.applicable]));
    return (before.discount_codes || []).filter(d => codeKey(d.code) !== codeKey(except)).every(d =>
      actual.has(codeKey(d.code)) && (!requireApplicable || d.applicable !== true || actual.get(codeKey(d.code)) === true));
  }
  function preservedLines(before, after, config, checkPrices = true) {
    const sum = (cart, key, field) => cart.items.filter(i => identity(i) === key).reduce((n, i) => n + Number(i[field] || 0), 0);
    return before.items.every(item => {
      const key = identity(item);
      return sum(after, key, 'quantity') === sum(before, key, 'quantity') &&
        (!checkPrices || Number(item.variant_id) === Number(config.variant) || sum(after, key, 'final_line_price') <= sum(before, key, 'final_line_price'));
    });
  }
  class Engine {
    constructor({ rules, api, pack = null, glowCart = null, lock = run => run(), onBusy = () => {}, onState = () => {} }) {
      this.rules = rules; this.api = api; this.lock = lock; this.onBusy = onBusy; this.onState = onState;
      this.pack = pack;
      this.glowCart = glowCart;
      this.tail = Promise.resolve(); this.pending = 0; this.cart = null; this.aliases = new Map();
    }
    queue(action, preserveGiftsOnFailure = false) {
      this.pending++; this.onBusy(true);
      const work = this.tail.then(() => this.lock(async () => {
        try {
          let cart = await this.api.read();
          cart = await action(cart) || cart;
          cart = await this.reconcileSecondKit(cart);
          cart = await this.reconcile(cart);
          cart = await this.reconcileSecondKit(cart);
          this.cart = cart; await this.onState(cart);
          return cart;
        } catch (error) {
          if (error.recoveredCart) {
            this.cart = error.recoveredCart; await this.onState(this.cart);
            throw error;
          }
          try {
            let cart = await this.api.read();
            const glowOffer = this.glowCart?.secondKit;
            const glowPending = glowSecondItems(cart, this.glowCart).length ||
              (glowOffer?.ownerAttribute && codeKey(cart.attributes?.[glowOffer.ownerAttribute]) === codeKey(glowOffer.discountCode));
            if (preserveGiftsOnFailure || glowPending) {
              // A failed Glow read/cleanup cannot justify deleting another
              // product's confirmed free units. Keep the cart and stop checkout.
              error.unsafe = true; error.recoveredCart = cart;
            } else {
              const gifts = cart.items.filter(i => prop(i, 'role') === 'gift');
              if (gifts.length) cart = await this.api.update({ updates: Object.fromEntries(gifts.map(i => [i.key, 0])) });
            }
            this.cart = cart; await this.onState(cart);
          } catch { error.unsafe = true; }
          throw error;
        }
      }));
      const finished = work.finally(() => { this.pending--; this.onBusy(this.pending > 0); });
      this.tail = finished.catch(() => {});
      return finished;
    }
    async reconcileSecondKit(cart) {
      try { return await this.reconcileSecondKitNow(cart); }
      catch (error) {
        if (error.recoveredCart) throw error;
        error.code = 'glow_second_recovery';
        error.message = 'The bundle discount could not be checked safely. Please retry before checking out.';
        error.unsafe = true;
        // Read-only recovery, including failures before/after owned-code cleanup.
        // Always use recoveredCart so the generic free-gift fallback is bypassed.
        try { cart = await this.api.read(); } catch {}
        error.recoveredCart = cart;
        throw error;
      }
    }
    async reconcileSecondKitNow(cart) {
      const config = this.glowCart, offer = config?.secondKit;
      if (!offer?.property || !offer.ownerAttribute || !offer.discountCode) return cart;
      let seconds = glowSecondItems(cart, config);
      const owned = codeKey(cart.attributes?.[offer.ownerAttribute]) === codeKey(offer.discountCode);
      if (!seconds.length && !owned) return cart;
      if (!Array.isArray(cart.discount_codes)) cart = await this.api.read();
      if (!seconds.length) {
        // /cart/clear leaves attributes and codes behind. Remove only the code
        // this flow owns, never a customer-entered or unrelated discount code.
        const before = cart;
        cart = await this.api.update({ discount: cartCodes(cart).filter(c => codeKey(c) !== codeKey(offer.discountCode)).join(','),
          attributes: { [offer.ownerAttribute]: '' } });
        if (!Array.isArray(cart.discount_codes)) cart = await this.api.read();
        if (cartCodes(cart).some(c => codeKey(c) === codeKey(offer.discountCode)) ||
          codeKey(cart.attributes?.[offer.ownerAttribute]) === codeKey(offer.discountCode) ||
          !preservedCodes(before, cart, offer.discountCode, false)) {
          const error = new Error('The bundle discount could not be updated. Please retry before checking out.');
          error.unsafe = true; error.recoveredCart = cart; throw error;
        }
        return cart;
      }
      if (glowSecondVerified(cart, config)) return cart;
      seconds = glowSecondItems(cart, config);
      const updates = Object.fromEntries(seconds.map(i => [i.key, 0]));
      const cleanup = owned ? { discount: cartCodes(cart).filter(c => codeKey(c) !== codeKey(offer.discountCode)).join(','),
        attributes: { [offer.ownerAttribute]: '' } } : {};
      const failure = new Error('The second bundle offer changed, so the extra bundle was removed. Please review your cart before checkout.');
      failure.code = 'glow_second_removed';
      try {
        const before = cart;
        cart = await this.api.update({ updates, ...cleanup });
        if (!Array.isArray(cart.discount_codes)) cart = await this.api.read();
        const kept = { ...before, items: before.items.filter(i => !seconds.includes(i)) };
        if (glowSecondItems(cart, config).length || !preservedLines(kept, cart, config, false) ||
          !preservedCodes(before, cart, owned ? offer.discountCode : '', false)) throw new Error('Recovery could not be confirmed');
      } catch {
        failure.message = 'We could not confirm the second bundle price. Please retry before checking out.';
        failure.unsafe = true;
        try { cart = await this.api.read(); } catch {}
      }
      failure.recoveredCart = cart;
      throw failure;
    }
    addSecondKit(expectedPrice) {
      return this.queue(async cart => {
        const config = this.glowCart, offer = glowSecondOffer(cart, config);
        // A second queued click and a stale card are harmless no-ops.
        if (!offer || Number(expectedPrice) !== offer.price) return cart;
        const before = cart;
        const oldCodes = cartCodes(before);
        const alreadyEntered = oldCodes.some(c => codeKey(c) === codeKey(offer.discountCode));
        const nextCodes = [...oldCodes, ...(alreadyEntered ? [] : [offer.discountCode])];
        const properties = { _mrv_bf_source: 'cart-upsell', [offer.property]: offer.discountCode,
          _mrv_glow_second_price: String(offer.price),
          _mrv_glow_second_tx: root.crypto?.randomUUID?.() || Date.now().toString(36) + Math.random().toString(36).slice(2) };
        const addedIdentity = identity({ id: config.variant, properties });
        let attempted = false, codeAttempted = false;
        try {
          const product = await this.api.product(config.handle);
          const variant = product.variants.find(v => Number(v.id) === Number(config.variant));
          if (String(product.id) !== String(config.product) || !variant?.available || variant.price !== offer.basePrice)
            throw new Error('The bundle offer changed. Reload your cart to see the current price.');
          attempted = true;
          await this.api.add([{ id: config.variant, quantity: 1, properties }]);
          codeAttempted = true;
          cart = await this.api.update({ discount: nextCodes.join(','),
            ...(!alreadyEntered ? { attributes: { [offer.ownerAttribute]: offer.discountCode } } : {}) });
          if (!Array.isArray(cart.discount_codes)) cart = await this.api.read();
          if (quantity(cart.items.filter(i => identity(i) === addedIdentity)) !== 1 ||
            cart.item_count !== before.item_count + 1 || !glowSecondVerified(cart, config) ||
            !preservedCodes(before, cart) || !preservedLines(before, cart, config) ||
            cart.total_price > before.total_price + offer.price) throw new Error('The R999 bundle price could not be confirmed.');
          return cart;
        } catch (cause) {
          const failure = new Error(attempted ? 'The second bundle offer could not be applied. Your original cart has been kept.' : cause.message);
          failure.code = 'glow_second_failed';
          try {
            cart = await this.api.read();
            if (attempted) {
              const additions = cart.items.filter(i => identity(i) === addedIdentity);
              const remaining = Math.max(0, quantity(additions) - 1);
              const updates = Object.fromEntries(additions.map((i, index) => [i.key, index === 0 ? remaining : 0]));
              const cleanup = {};
              if (codeAttempted) {
                const current = cartCodes(cart).filter(c => alreadyEntered || codeKey(c) !== codeKey(offer.discountCode));
                cleanup.discount = [...new Map([...oldCodes, ...current].map(c => [codeKey(c), c])).values()].join(',');
                if (!alreadyEntered) cleanup.attributes = { [offer.ownerAttribute]: before.attributes?.[offer.ownerAttribute] || '' };
              }
              if (additions.length || codeAttempted) cart = await this.api.update({ updates, ...cleanup });
              if (!Array.isArray(cart.discount_codes)) cart = await this.api.read();
              if (quantity(cart.items.filter(i => identity(i) === addedIdentity)) !== 0 ||
                !preservedCodes(before, cart) || !preservedLines(before, cart, config) ||
                cart.item_count !== before.item_count || cart.total_price > before.total_price) throw new Error('Recovery could not be confirmed');
            }
          } catch {
            failure.message = 'We could not restore the second bundle offer safely. Please retry before checking out.';
            failure.unsafe = true;
            try { cart = await this.api.read(); } catch {}
          }
          failure.recoveredCart = cart;
          throw failure;
        }
      }, true);
    }
    refresh() {
      if (this.refreshPromise && this.refreshTail === this.tail) return this.refreshPromise;
      const result = this.queue(cart => cart);
      this.refreshPromise = result;
      this.refreshTail = this.tail;
      const clear = () => { if (this.refreshPromise === result) this.refreshPromise = null; };
      result.then(clear, clear);
      return result;
    }
    upgradePack(target, expectedPrice) {
      return this.queue(async cart => {
        const item = cart.items.find(i => i.key === target.key) || cart.items.find(i => identity(i) === target.identity);
        const shown = packOffer(cart, item, this.pack);
        if (!shown || shown.price !== Number(expectedPrice)) return cart;
        const original = identity(item);
        const replacement = identity({ ...item, variant_id: shown.target });
        const previousTarget = quantity(cart.items.filter(i => identity(i) === replacement));
        const beforeTotal = cart.total_price;
        let attempted = false;
        try {
          const product = await this.api.product(this.pack.handle);
          const fresh = { ...this.pack, variants: Object.fromEntries(Object.entries(this.pack.variants).map(([id, v]) => {
            const actual = product.variants.find(x => String(x.id) === id);
            return [id, { ...v, price: actual?.price || 0, available: Boolean(actual?.available) }];
          })) };
          const offer = String(product.id) === String(this.pack.product) && packOffer(cart, item, fresh);
          if (!offer || offer.price !== shown.price || !offer.to.available) throw Object.assign(new Error('The pack offer changed. Reload your cart to see the latest price and availability.'), { code: 'pack_changed' });
          attempted = true;
          const added = await this.api.add([{ id: offer.target, quantity: offer.count, properties: item.properties || {} }]);
          cart = added?.items && Number.isFinite(added.item_count) ? added : await this.api.read();
          if (quantity(cart.items.filter(i => identity(i) === replacement)) !== previousTarget + offer.count) throw new Error('Pack quantity could not be confirmed');
          cart = await this.api.update({ updates: Object.fromEntries(cart.items.filter(i => identity(i) === original).map(i => [i.key, 0])) });
          cart = await this.reconcile(cart);
          if (cart.items.some(i => identity(i) === original) || quantity(cart.items.filter(i => identity(i) === replacement)) !== previousTarget + offer.count || cart.total_price > beforeTotal + offer.price) throw new Error('Pack price could not be confirmed');
          return cart;
        } catch (error) {
          cart = await this.api.read();
          try {
            if (attempted) {
              const targets = cart.items.filter(i => identity(i) === replacement);
              if (targets.length) cart = await this.api.update({ updates: Object.fromEntries(targets.map((i, n) => [i.key, n === 0 ? previousTarget : 0])) });
              const remaining = quantity(cart.items.filter(i => identity(i) === original));
              if (remaining < shown.count) {
                await this.api.add([{ id: item.variant_id, quantity: shown.count - remaining, properties: item.properties || {} }]);
                cart = await this.api.read();
              }
              if (quantity(cart.items.filter(i => identity(i) === original)) !== shown.count || quantity(cart.items.filter(i => identity(i) === replacement)) !== previousTarget) throw new Error('Recovery incomplete');
            }
            error.code = error.code || 'pack_failed';
          } catch { error.code = 'pack_recovery'; error.unsafe = true; cart = await this.api.read(); }
          error.recoveredCart = cart;
          throw error;
        }
      });
    }
    claim(target, expectedPrice = 0) {
      return this.queue(async cart => {
        const item = cart.items.find(i => i.key === target.key) || cart.items.find(i => identity(i) === (this.aliases.get(target.identity) || target.identity));
        return this.claimNow(cart, item, Number(expectedPrice));
      });
    }
    async claimNow(cart, item, expectedPrice = 0) {
      const offer = claimOffer(cart, item, this.rules);
      if (!offer || offer.price !== expectedPrice) return cart;
      item = offer.item;
      const beforeTotal = cart.total_price;
      const previousPause = paused(cart)[offer.group];
      const nextIdentity = identity({ ...item, properties: offer.properties });
      try {
        if (identity(item) !== nextIdentity) {
          for (const original of cart.items.filter(i => identity(i) === identity(item))) {
            cart = await this.api.change({ id: original.key, quantity: original.quantity, properties: offer.properties });
          }
          this.aliases.set(identity(item), nextIdentity);
        }
        if (previousPause) {
          const p = paused(cart); delete p[offer.group];
          cart = await this.api.update({ attributes: { [PAUSED]: JSON.stringify(p) } });
        }
        const additions = [{ id: item.variant_id, quantity: offer.more, properties: { ...offer.properties, [PREFIX + 'role']: 'gift' } }];
        if (offer.mode === 'upgrade') additions.unshift({ id: item.variant_id, quantity: 1, properties: offer.properties });
        const added = await this.api.add(additions);
        cart = added?.items && Number.isFinite(added.item_count) ? added : await this.api.read();
        cart = await this.reconcile(cart);
        const group = plan(cart, this.rules).find(g => g.id === offer.group);
        if (group?.current === offer.current + offer.more && group.gifts.every(i => verified(i, offer.rule)) && cart.total_price <= beforeTotal + offer.price) return cart;
        throw new Error('Bonus unavailable');
      } catch (error) {
        cart = await this.api.read();
        const gifts = cart.items.filter(i => prop(i, 'role') === 'gift' && prop(i, 'group') === offer.group);
        if (gifts.length) cart = await this.api.update({ updates: Object.fromEntries(gifts.map((i, index) => [i.key, index === 0 ? offer.current : 0])) });
        if (offer.mode === 'upgrade') {
          const paid = cart.items.filter(i => identity(i) === nextIdentity);
          if (paid.length) cart = await this.api.update({ updates: Object.fromEntries(paid.map((i, index) => [i.key, index === 0 ? 1 : 0])) });
        }
        const p = paused(cart);
        if (previousPause) p[offer.group] = previousPause; else delete p[offer.group];
        cart = await this.api.update({ attributes: { [PAUSED]: JSON.stringify(p) } });
        cart = await this.reconcile(cart);
        const failure = new Error('The bonus could not be confirmed. Your original quantity has been kept.');
        failure.code = 'claim_failed'; failure.recoveredCart = cart;
        throw failure;
      }
    }
    pause(cart, group, reason) {
      const p = paused(cart); p[group] = reason;
      return this.api.update({ attributes: { [PAUSED]: JSON.stringify(p) } });
    }
    restore(group) {
      return this.queue(cart => this.claimNow(cart, groups(cart, this.rules).find(g => g.id === group)?.paid[0]));
    }
    async removeGifts(cart, ids) {
      const gifts = cart.items.filter(i => prop(i, 'role') === 'gift' && ids.includes(prop(i, 'group')));
      return gifts.length ? this.api.update({ updates: Object.fromEntries(gifts.map(i => [i.key, 0])) }) : cart;
    }
    async reconcile(cart) {
      const valid = groups(cart, this.rules);
      const invalid = cart.items.filter(i => prop(i, 'role') === 'gift' && !valid.some(g => g.gifts.includes(i)));
      if (invalid.length) cart = await this.api.update({ updates: Object.fromEntries(invalid.map(i => [i.key, 0])) });
      const initial = plan(cart, this.rules);
      for (const intent of initial) {
        let g = plan(cart, this.rules).find(x => x.id === intent.id);
        if (!g) continue;
        if (g.current !== g.target) {
          cart = await this.removeGifts(cart, [g.id]);
          if (g?.target > 0 && g.paid.length) {
            const first = g.paid[0];
            const properties = { ...first.properties, [PREFIX + 'role']: 'gift' };
            try {
              const added = await this.api.add([{ id: first.variant_id, quantity: g.target, properties }]);
              cart = added?.items && Number.isFinite(added.item_count) ? added : await this.api.read();
            } catch (error) {
              cart = await this.api.read();
              cart = await this.removeGifts(cart, [g.id]);
              cart = await this.pause(cart, g.id, error.status === 422 ? 'unavailable' : 'retry');
            }
          }
        }
      }
      // Shopify can move an equal-price discount onto a newer line. Reinsert the
      // owned bonus once, then trust only Shopify's returned free-item allocation.
      // This is bounded; it cannot repair a collection-wide or conflicting rule.
      const displaced = plan(cart, this.rules).filter(g => g.target > 0 && g.current === g.target && g.gifts.some(i => !verified(i, g.rule)));
      for (const g of displaced) {
        const first = g.paid[0];
        if (!first) continue;
        cart = await this.removeGifts(cart, [g.id]);
        try {
          const added = await this.api.add([{ id: first.variant_id, quantity: g.target, properties: { ...first.properties, [PREFIX + 'role']: 'gift' } }]);
          cart = added?.items && Number.isFinite(added.item_count) ? added : await this.api.read();
        } catch (error) {
          cart = await this.api.read();
          cart = await this.removeGifts(cart, [g.id]);
          cart = await this.pause(cart, g.id, error.status === 422 ? 'unavailable' : 'retry');
        }
      }
      const bad = plan(cart, this.rules).filter(g => g.gifts.some(i => !verified(i, g.rule)) || (g.current > 0 && g.current !== g.target));
      if (bad.length) {
        cart = await this.removeGifts(cart, bad.map(g => g.id));
        const p = paused(cart); bad.forEach(g => { p[g.id] = 'discount'; });
        cart = await this.api.update({ attributes: { [PAUSED]: JSON.stringify(p) } });
      }
      const unsafe = cart.items.some(i => prop(i, 'role') === 'gift' && (!itemRule(i, this.rules) || !verified(i, itemRule(i, this.rules))));
      if (unsafe) { const e = new Error('Bonus discount could not be confirmed. Please retry.'); e.unsafe = true; throw e; }
      return cart;
    }
    add(items, context) {
      return this.queue(async cart => {
        // A fresh PDP addition may join an older, same-variant cart line. New
        // recommendations and upsells carry source=other and cannot be adopted.
        if (context && !context.upsell && !context.complete) {
          for (const incoming of items) {
            const rule = variantRule(this.rules[context.offer], incoming.id);
            if (!rule || !context.variants.includes(Number(incoming.id))) continue;
            const candidates = cart.items.filter(i => Number(i.variant_id) === Number(incoming.id) && legacyOffer(i, this.rules) && stable(userProperties(i)) === stable(incoming.properties || {}));
            for (const old of candidates) {
              const properties = taggedProperties(old, context.offer, rule, 'paid', 'legacy-cart');
              // Use the PDP source so this fresh addition merges with its older unit.
              properties[PREFIX + 'source'] = 'product-page';
              const before = identity(old);
              cart = await this.api.change({ id: old.key, quantity: old.quantity - nativeFreeUnits(old, rule), properties });
              this.aliases.set(before, identity({ ...old, properties }));
            }
          }
        }
        const prepared = items.map(item => {
          if (context?.upsell) return { ...item, properties: { ...item.properties, [PREFIX + 'source']: 'cart-upsell' } };
          const rule = context && variantRule(this.rules[context.offer], item.id);
          if (!rule || !context.variants.includes(Number(item.id)) || item.selling_plan || !rule.products.includes(String(context.product))) return { ...item, properties: { ...item.properties, [PREFIX + 'source']: 'other' } };
          const role = context.complete && Number(item.quantity) === rule.buy + rule.free ? 'bundle' : 'paid';
          const properties = taggedProperties(item, context.offer, rule, role, 'product-page');
          const existing = cart.items.find(i => prop(i, 'group') === properties[PREFIX + 'group'] && prop(i, 'role') === role);
          if (existing) properties[PREFIX + 'source'] = prop(existing, 'source');
          return { ...item, properties };
        });
        const added = await this.api.add(glowOriginItems(cart, prepared, this.glowCart));
        return added?.items && Number.isFinite(added.item_count) ? added : this.api.read();
      });
    }
    change(target, value, mode = 'paid') {
      if (!Number.isSafeInteger(value) || (mode !== 'step' && value < 0) || (mode === 'step' && ![-1, 1].includes(value))) return Promise.reject(new Error('Enter a valid quantity.'));
      return this.queue(async cart => {
        let item = cart.items.find(i => i.key === target.key) || cart.items.find(i => identity(i) === (this.aliases.get(target.identity) || target.identity));
        if (!item) return cart;
        let group = prop(item, 'group');
        if (prop(item, 'role') === 'gift') {
          cart = await this.removeGifts(cart, [group]);
          return this.pause(cart, group, 'removed');
        }
        const oldIdentity = identity(item);
        const rowItems = displayItems(cart, item);
        if (mode !== 'step' && value === 0) return this.api.update({ updates: Object.fromEntries(rowItems.map(i => [i.key, 0])) });
        const siblings = rowItems.filter(i => prop(i, 'role') !== 'gift');
        const rowKeys = new Set(siblings.map(i => i.key));
        const previousGifts = quantity(rowItems.filter(i => prop(i, 'role') === 'gift'));
        const logicalQuantity = quantity(siblings);
        const legacy = ((mode === 'step' && value > 0) || (mode !== 'step' && value > logicalQuantity)) ? legacyOffer(item, this.rules) : null;
        const bundleRule = siblings.some(i => prop(i, 'role') === 'bundle' || nativeFreeUnits(i, itemRule(i, this.rules)) > 0) ? itemRule(item, this.rules) : null;
        const adoption = legacy || (bundleRule && { offer: prop(item, 'offer'), rule: bundleRule });
        let properties;
        let authorized = 0;
        let paid = logicalQuantity;
        if (adoption && value !== 0) {
          authorized = siblings.reduce((n, i) => n + nativeFreeUnits(i, adoption.rule), 0);
          paid -= authorized;
          properties = taggedProperties(item, adoption.offer, adoption.rule, 'paid', legacy ? 'legacy-cart' : prop(item, 'source'));
          group = properties[PREFIX + 'group'];
        }
        const managed = prop(item, 'role') === 'paid' || Boolean(properties);
        if (mode === 'step') value = Math.max(0, paid + value);
        else if (mode === 'total' && managed && value > 0) {
          const totalFor = n => {
            const simulated = { ...cart, items: [...cart.items.filter(i => !rowKeys.has(i.key)), { ...item, quantity: n, line_level_discount_allocations: [], properties: properties || item.properties }] };
            const g = plan(simulated, this.rules).find(g => g.id === group);
            return n + Math.min(authorized + previousGifts, g?.capacity || 0);
          };
          let low = 0, high = value;
          while (low < high) { const mid = Math.floor((low + high) / 2); if (totalFor(mid) >= value) high = mid; else low = mid + 1; }
          value = low;
        }
        // Native discounts can split one logical item into multiple Ajax lines.
        // Change every sibling together, never mistake a split for extra intent.
        const nextIdentity = properties ? identity({ ...item, properties }) : oldIdentity;
        if (properties) {
          const oldIdentities = new Set(siblings.map(identity));
          for (let attempt = 0; attempt < 50; attempt++) {
            const remaining = displayItems(cart, item).find(i => prop(i, 'role') !== 'gift' && identity(i) !== nextIdentity);
            if (!remaining) break;
            cart = await this.api.change({ id: remaining.key, quantity: remaining.quantity, properties });
          }
          if (displayItems(cart, item).some(i => prop(i, 'role') !== 'gift' && identity(i) !== nextIdentity)) throw new Error('The cart changed. Please retry.');
          oldIdentities.forEach(id => this.aliases.set(id, nextIdentity));
        }
        const current = cart.items.filter(i => identity(i) === nextIdentity);
        if (!current.length) return cart;
        const updates = Object.fromEntries(current.map((i, index) => [i.key, index === 0 ? value : 0]));
        const simulated = { ...cart, items: cart.items.map(i => updates[i.key] !== undefined ? { ...i, quantity: updates[i.key], line_level_discount_allocations: [] } : i).filter(i => i.quantity > 0) };
        const next = managed ? plan(simulated, this.rules).find(g => g.id === group) : null;
        if (next && authorized) next.target = Math.min(authorized + previousGifts, next.capacity);
        const gifts = cart.items.filter(i => prop(i, 'role') === 'gift' && prop(i, 'group') === group);
        if (next?.target > 0 && !gifts.length) {
          const first = current[0];
          cart = await this.api.update({ updates });
          const added = await this.api.add([{ id: first.variant_id, quantity: next.target, properties: { ...first.properties, [PREFIX + 'role']: 'gift' } }]);
          return added?.items && Number.isFinite(added.item_count) ? added : this.api.read();
        }
        gifts.forEach((i, index) => { updates[i.key] = index === 0 ? (next?.target || 0) : 0; });
        return this.api.update({ updates });
      });
    }
  }
  const exported = { Engine, prop, groups, plan, identity, paused, verified, variantRule, itemRule, nativeFreeUnits, legacyOffer, claimOffer, packOffer, previewQuantity, displayItems, glowCartScope, glowOriginItems, glowUpsellHandles, glowSecondOffer, glowSecondItems, glowSecondVerified, glowCodeAmount };
  if (typeof module !== 'undefined' && module.exports) module.exports = exported;
  else root.MirovaGiftCore = exported;
})(typeof window !== 'undefined' ? window : globalThis);


